import { useState, useRef, useEffect } from 'react';
import { useAppDataContext } from '../../context/AppDataContext';
import { useProducts } from '../../context/ProductsContext';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/index.jsx';
import { chatWithAssistant } from '../../services/claudeApi';
import Icon from '../common/Icon';

export default function AIAssistant({ isModal = false, onClose, onOpenIntake }) {
  const { orders, setOrders, logs, setLogs, customers, skuMap, setSkuMap, bottles, shipments, setShipments, tasks, setTasks, users } = useAppDataContext();
  const { bjProducts, setBjProducts, marucoProducts, setMarucoProducts } = useProducts();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(isModal);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const chatEndRef = useRef(null);
  const galleryRef = useRef(null);

  const products = [...bjProducts, ...marucoProducts];

  // Normalize wine type from AI (Red/R/red → R, White/W → W, etc.)
  const normalizeWineType = (type) => {
    const map = { red: 'R', r: 'R', white: 'W', w: 'W', sparkling: 'S', s: 'S',
      champagne: 'SP', sp: 'SP', rosé: 'P', rose: 'P', p: 'P', orange: 'O', o: 'O' };
    return map[(type || '').toLowerCase()] || (type || 'R').toUpperCase();
  };

  // Resolve a product by ID, fuzzy search, or auto-create if isNew
  const resolveOrCreateProduct = (item) => {
    // 1. Exact ID match
    let found = products.find(p => p.id === item.productId);
    if (found) return { product: found, wasCreated: false };

    // 2. Fuzzy search by name + vintage across ALL products
    const searchName = (item.name || '').toLowerCase();
    const searchVintage = item.vintage || '';
    if (searchName) {
      found = products.find(p => {
        const pName = (p.name || '').toLowerCase();
        if (searchVintage && p.vintage && p.vintage !== searchVintage) return false;
        // Check if product name contains the search keywords
        const keywords = searchName.split(/\s+/).filter(w => w.length > 2);
        return keywords.length > 0 && keywords.every(kw => pName.includes(kw));
      });
      if (found) return { product: found, wasCreated: false };
    }

    // 3. Auto-create if AI provided structured data
    if (item.isNew && item.wineryCode) {
      // Detect brand: use AI hint if provided, otherwise default to MARUCO for auto-created products
      const brand = (item.brand || 'MARUCO').toUpperCase() === 'BJ' ? 'BJ' : 'MARUCO';
      const wineryCode = (item.wineryCode || '').toUpperCase();
      const vintage = item.vintage || new Date().getFullYear().toString();
      const type = normalizeWineType(item.type || 'R');
      const location = item.location || 'LA2';
      const price = item.price || 0;
      const cost = Math.floor(price * 0.6);

      // Generate SKU: {WINERY_CODE}-{VINTAGE_LAST_2}-{SEQ}
      // Match NewProductModal logic (line 231)
      const vintageLast2 = vintage.slice(-2);
      const skuPrefix = `${wineryCode}-`;
      const existingSkuCount = Object.keys(skuMap || {}).filter(s => s.startsWith(skuPrefix)).length;
      const sku = `${wineryCode}-${vintageLast2}-${String(existingSkuCount + 1).padStart(2, '0')}`;

      // Register in skuMap
      const wineName = item.name || `${item.winery || wineryCode}`;
      setSkuMap(prev => ({ ...prev, [sku]: { wine_name: wineName, vintage, winery_id: wineryCode } }));

      // Product groupKey (same as deriveProductsFromBottles)
      const groupKey = `${brand}|${sku}|${location}`;

      // Create product and add to brand array
      // stock = qty so bottles are created, then executeAction will deduct to 0
      const newProduct = {
        id: groupKey,
        _groupKey: groupKey,
        sku,
        type,
        name: wineName,
        winery: wineryCode,
        vintage,
        location,
        stock: item.qty || 1,
        cost,
        price,
      };

      if (brand === 'BJ') {
        setBjProducts(prev => [...prev, newProduct]);
      } else {
        setMarucoProducts(prev => [...prev, newProduct]);
      }

      // Add an 入庫 log for the auto-created product
      const autoLog = {
        id: `auto-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        date: new Date().toISOString().split('T')[0],
        slipNo: `NEW-${new Date().toISOString().split('T')[0].slice(2).replace(/-/g, '')}-${String(existingSkuCount + 1).padStart(3, '0')}`,
        productId: groupKey,
        productName: wineName,
        type: t('ai.stockIn'),
        qty: item.qty || 1,
        cost,
        price: 0,
        total: cost * (item.qty || 1),
        partner: t('ai.autoRegistration'),
        staff: user.name,
      };
      setLogs(prev => [autoLog, ...prev]);

      return { product: { ...newProduct, id: groupKey }, wasCreated: true };
    }

    return { product: null, wasCreated: false };
  };

  useEffect(() => {
    if (expanded) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, expanded]);

  // Build system prompt with current data context
  const buildSystemPrompt = () => {
    const today = new Date().toISOString().split('T')[0];
    const productList = products.slice(0, 50).map(p =>
      `${p.id}: ${p.name} (${p.winery}, ${p.vintage || '?'}) 在庫:${p.stock} ¥${p.price || '?'} 位置:${p.location}`
    ).join('\n');

    return `あなたは恵比寿マルコ（Vin Maruco）ワインショップのAIアシスタントです。
管理者が自然言語の会話やチャットのスクリーンショットを通じて業務操作を実行するのを支援します。

## 本日の日付: ${today}
## 現在のユーザー: ${user.name}（${user.role === 'admin' ? '管理者' : 'スタッフ'}）

## 在庫商品リスト（${products.length}件中${Math.min(50, products.length)}件表示）:
${productList}
注意：上記は一部の商品のみです。お客様が購入希望のワインがリストにない場合は、isNew: true を設定して詳細情報を提供してください。

## 実行可能な操作:
1. **販売注文の作成** - お客様との会話や要望に基づいて注文を生成
2. **入庫** - 新しい在庫を追加
3. **在庫調整** - 在庫数量を修正
4. **情報照会** - 商品・在庫情報を確認

## 回答形式:
まず自然言語で回答し、操作の実行が必要な場合は回答の末尾に以下のJSONブロックを追加:

\`\`\`action
{
  "type": "create_order" | "stock_in" | "stock_adjust" | "pre_stock" | "info",
  "data": { ... }
}
\`\`\`

### create_order のdata形式:
{
  "date": "YYYY-MM-DD（任意、デフォルトは本日。「昨日」なら昨日の日付、「一昨日」なら一昨日を使用）",
  "items": [各項目は以下の形式],
  "customer": "顧客名",
  "paymentMethod": "cash|card|transit|paypay|wechat|alipay|hsbc|japan_bank",
  "taxType": "inclusive|exclusive",
  "notes": "備考"
}

items内の各項目:
- 在庫リストに一致する場合: { "productId": "リスト内の商品ID", "name": "商品名", "qty": 数量, "price": 単価 }
- リストにない場合: { "productId": "NEW", "name": "完全なワイン名", "qty": 数量, "price": 単価, "isNew": true, "winery": "ワイナリー正式名", "wineryCode": "2-3文字の略称(例:DRC,LB,NF)", "vintage": "ヴィンテージ(例:2020)", "type": "R/W/S/SP/P/O", "brand": "BJまたはMARUCO" }

### stock_in のdata形式:
{
  "date": "YYYY-MM-DD（任意、デフォルトは本日）",
  "items": [各項目は以下の形式],
  "source": "仕入先（スタッフの場合は空文字でOK）"
}

items内の各項目:
- 在庫リストに一致する場合: { "productId": "商品ID", "name": "商品名", "qty": 数量, "cost": 原価（スタッフ不明の場合は0） }
- 新規商品の場合: { "productId": "NEW", "name": "完全なワイン名", "qty": 数量, "cost": 原価（スタッフ不明の場合は0）, "isNew": true, "winery": "ワイナリー正式名", "wineryCode": "2-3文字の略称", "vintage": "ヴィンテージ", "type": "R/W/S/SP/P/O", "brand": "BJまたはMARUCO" }

### stock_adjust のdata形式:
{
  "items": [{ "productId": "商品ID", "name": "商品名", "newStock": 新在庫数, "reason": "理由" }]
}

### pre_stock のdata形式（入荷予定登録 - 仕入書/請求書の画像から入荷予定を作成）:
{
  "supplier": "仕入先名",
  "invoiceNumber": "請求書番号（あれば）",
  "invoiceDate": "YYYY-MM-DD（請求書日付）",
  "expectedDate": "YYYY-MM-DD（入荷予定日、不明なら省略）",
  "items": [
    { "name": "完全なワイン名", "qty": 数量, "unitPrice": 単価（税込原価）, "productId": "既存商品IDまたはnull" }
  ]
}

## pre_stock のルール:
- 仕入書/請求書/発注書の画像を受信した場合、すべての明細行を抽出すること
- 既存商品リストとマッチングし、一致する場合はproductIdを設定
- 一致しない場合はproductId: nullを設定
- **在庫は追加しない**（入荷予定として登録するのみ。実際の入庫はスタッフが検品後に実行）
- 操作実行前に必ず内容を確認すること

## wineryCode 例:
DRC=Domaine de la Romanée-Conti, LB=Comte Liger-Belair, NF=Nicolas Faure, CB=Cedric Bouchard, SC=Sylvain Cathiard, PR=Prieuré Roch, DT=Domaine Takahiko, AC=Andrea Cervini

## type（タイプ）説明:
R=赤ワイン, W=白ワイン, S=スパークリング, SP=シャンパン, P=ロゼ, O=オレンジワイン

## 重要なルール:
- 商品名で検索し、最も近い商品IDを自動マッチング（IDの形式は「ブランド|SKU|ロケーション」）
- 価格が不明な場合は在庫リスト内の価格を使用
- **絶対に偽のIDを生成しないこと**（TEMP-xxx、XX-XX等の形式は不可）。見つからない場合は isNew: true を設定
- 操作実行前に必ず確認すること（「この注文を作成してよろしいですか？」）
- 画像を受信した場合はチャットのスクリーンショットまたは注文伝票として分析
- 日本語で回答すること
- 金額はデフォルトで税込（内税）
- **日付の処理**: ユーザーが「昨日」「一昨日」「先週の○曜日」等の過去の日時を言及した場合、dataに正しい"date"フィールド（YYYY-MM-DD形式）を設定すること。dateを設定しない場合はデフォルトで本日。
- **ブランド判定**: 商品IDの接頭辞「BJ|」はBottles Japan、「MARUCO|」はVin Maruco。ロケーションA1-F8はBJ倉庫、LA-RE始まりはMARUCO倉庫。新規商品作成時は実際の状況に応じてbrandフィールドを設定すること。

## スタッフ入庫のルール（重要）:
- ユーザーがスタッフ（管理者でない）の場合:
  - "source"（仕入先）は省略可能 — スタッフに聞かないこと
  - "cost"（原価）は省略可能（0で設定）— スタッフに聞かないこと
  - スタッフに必要な情報は**商品名と数量のみ**
  - 画像から商品を認識した場合、商品名と数量だけ確認して入庫を実行すること
- ユーザーが管理者の場合:
  - 従来通り source と cost を確認すること`;
  };

  // Parse action from AI response
  const parseAction = (text) => {
    const actionMatch = text.match(/```action\s*([\s\S]*?)```/);
    if (actionMatch) {
      try {
        return JSON.parse(actionMatch[1].trim());
      } catch { return null; }
    }
    return null;
  };

  // Get display text (without action block)
  const getDisplayText = (text) => {
    return text.replace(/```action[\s\S]*?```/g, '').trim();
  };

  // Send message (extraContents can be a single image block or an array of image blocks)
  const handleSend = async (extraContents = null) => {
    if ((!input.trim() && !extraContents) || loading) return;

    // Normalize to array
    const extras = extraContents ? (Array.isArray(extraContents) ? extraContents : [extraContents]) : [];

    const userContent = [];
    extras.forEach(img => userContent.push(img));
    if (input.trim()) {
      userContent.push({ type: 'text', text: input.trim() });
    }

    const imageCount = extras.length;
    const userMsg = {
      role: 'user',
      content: userContent.length === 1 && userContent[0].type === 'text' ? input.trim() : userContent,
      displayText: input.trim() || (imageCount > 1 ? `📷 ${t('ai.imagesAttached', { count: imageCount })}` : `📷 ${t('ai.imageAttached')}`),
      hasImage: imageCount > 0,
      imageCount,
    };

    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const apiMessages = newMessages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const reply = await chatWithAssistant({
        messages: apiMessages,
        systemPrompt: buildSystemPrompt(),
      });

      const action = parseAction(reply);
      const displayText = getDisplayText(reply);

      const assistantMsg = {
        role: 'assistant',
        content: reply,
        displayText,
        action,
      };

      setMessages([...newMessages, assistantMsg]);

      if (action && action.type !== 'info') {
        setPendingAction(action);
      }
    } catch (err) {
      setMessages([...newMessages, {
        role: 'assistant',
        content: err.message,
        displayText: `⚠️ ${err.message}`,
      }]);
    }
    setLoading(false);
  };

  // Compress image using canvas (max 1600px, quality 0.8)
  const compressImage = (file, maxSize = 1600, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve({
          base64: dataUrl.split(',')[1],
          mediaType: 'image/jpeg',
        });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle image upload (supports multiple files, auto-compress for API limits)
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';

    try {
      const imageBlocks = await Promise.all(
        files.map(async (file) => {
          const { base64, mediaType } = await compressImage(file);
          return {
            type: 'image',
            source: { type: 'base64', media_type: mediaType, data: base64 },
          };
        })
      );
      handleSend(imageBlocks);
    } catch (err) {
      console.error('Image compress error:', err);
    }
  };

  // Execute confirmed action
  const executeAction = (action) => {
    const defaultToday = new Date().toISOString().split('T')[0];
    const now = new Date();

    if (action.type === 'create_order') {
      const d = action.data;
      const today = d.date || defaultToday;
      const slipNo = `SLS-${today.slice(2).replace(/-/g, '')}-${String(orders.length + 1).padStart(3, '0')}`;

      // Resolve all items first (find existing or auto-create new products)
      const resolvedItems = [];
      const createdProducts = [];
      d.items.forEach(item => {
        const { product, wasCreated } = resolveOrCreateProduct(item);
        if (product) {
          resolvedItems.push({ ...item, productId: product.id, resolvedName: product.name || item.name });
          if (wasCreated) createdProducts.push(product);
        } else {
          resolvedItems.push({ ...item, unresolved: true });
        }
      });

      const subtotal = resolvedItems.reduce((s, i) => s + (i.price * i.qty), 0);
      const taxType = d.taxType || 'inclusive';
      let tax, total;
      if (taxType === 'inclusive') {
        tax = Math.floor(subtotal - subtotal / 1.1);
        total = subtotal;
      } else {
        tax = Math.floor(subtotal * 0.1);
        total = subtotal + tax;
      }

      const newOrder = {
        id: slipNo,
        date: today,
        items: resolvedItems.map(i => {
          // Detect brand from resolved product ID (format: "BRAND|SKU|LOC")
          const pid = i.productId || '';
          const itemBrand = pid.startsWith('BJ|') ? 'BJ' : pid.startsWith('MARUCO|') ? 'MARUCO' : 'BJ';
          return { id: pid, name: i.resolvedName || i.name, qty: i.qty, price: i.price, brand: itemBrand };
        }),
        subtotal: taxType === 'inclusive' ? subtotal - tax : subtotal,
        tax,
        total,
        taxType,
        taxRate: 10,
        paymentMethod: d.paymentMethod || 'cash',
        customer: d.customer || t('ai.storeCustomer'),
        staff: user.name,
        channel: t('ai.storeSales'),
        status: 'completed',
        createdAt: now.toISOString(),
        notes: d.notes || '',
      };
      setOrders(prev => [newOrder, ...prev]);

      const newLogs = [];
      resolvedItems.forEach((item, idx) => {
        newLogs.push({
          id: `${Date.now()}-${idx}`,
          date: today,
          slipNo,
          productId: item.productId,
          productName: item.resolvedName || item.name,
          type: t('ai.stockOut'),
          qty: item.qty,
          cost: 0,
          price: item.price,
          total: item.price * item.qty,
          channel: t('ai.storeSales'),
          taxType,
          paymentMethod: d.paymentMethod || 'cash',
          customer: d.customer || t('ai.storeCustomer'),
          staff: user.name,
          orderSubtotal: newOrder.subtotal,
          orderTax: tax,
          orderTotal: total,
          taxRate: 10,
        });

        if (!item.unresolved) {
          setBjProducts(prev => prev.map(p => p.id === item.productId ? { ...p, stock: Math.max(0, (p.stock || 0) - item.qty) } : p));
          setMarucoProducts(prev => prev.map(p => p.id === item.productId ? { ...p, stock: Math.max(0, (p.stock || 0) - item.qty) } : p));
        }
      });
      setLogs(prev => [...newLogs, ...prev]);

      let resultMsg = `✅ ${t('ai.orderCreated', { slipNo, total: total.toLocaleString() })}`;
      if (createdProducts.length > 0) {
        resultMsg += `\n🆕 ${t('ai.newProductsCreated', { count: createdProducts.length })}`;
      }
      const unresolvedCount = resolvedItems.filter(i => i.unresolved).length;
      if (unresolvedCount > 0) {
        resultMsg += `\n⚠️ ${t('ai.unmatchedItems', { count: unresolvedCount })}`;
      }
      return resultMsg;
    }

    if (action.type === 'stock_in') {
      const d = action.data;
      const today = d.date || defaultToday;
      const newLogs = [];
      const createdProducts = [];

      d.items.forEach((item, idx) => {
        // For stock_in with isNew, resolveOrCreateProduct handles creation with stock = qty
        // So we just need to resolve and add logs (no additional stock increment needed for new products)
        const { product, wasCreated } = resolveOrCreateProduct({ ...item, price: item.cost || 0 });
        const resolvedId = product ? product.id : item.productId;
        const resolvedName = product ? (product.name || item.name) : item.name;
        if (wasCreated) createdProducts.push(product);

        const slipNo = `IN-${today.slice(2).replace(/-/g, '')}-${String(idx + 1).padStart(3, '0')}`;
        newLogs.push({
          id: `${Date.now()}-${idx}`,
          date: today,
          slipNo,
          productId: resolvedId,
          productName: resolvedName,
          type: t('ai.stockIn'),
          qty: item.qty,
          cost: item.cost || 0,
          price: 0,
          total: (item.cost || 0) * item.qty,
          partner: d.source || '',
          staff: user.name,
        });

        // Only increment stock for existing products (new products already have correct stock from resolveOrCreateProduct)
        if (!wasCreated && product) {
          setBjProducts(prev => prev.map(p => p.id === resolvedId ? { ...p, stock: (p.stock || 0) + item.qty } : p));
          setMarucoProducts(prev => prev.map(p => p.id === resolvedId ? { ...p, stock: (p.stock || 0) + item.qty } : p));
        }
      });
      setLogs(prev => [...newLogs, ...prev]);

      let resultMsg = `✅ ${t('ai.stockInRecorded', { count: d.items.length })}`;
      if (createdProducts.length > 0) {
        resultMsg += `\n🆕 ${t('ai.newProductsCreated', { count: createdProducts.length })}`;
      }
      return resultMsg;
    }

    if (action.type === 'stock_adjust') {
      const d = action.data;
      d.items.forEach(item => {
        setBjProducts(prev => prev.map(p => p.id === item.productId ? { ...p, stock: item.newStock } : p));
        setMarucoProducts(prev => prev.map(p => p.id === item.productId ? { ...p, stock: item.newStock } : p));
      });

      return `✅ ${t('ai.stockAdjusted', { count: d.items.length })}`;
    }

    if (action.type === 'pre_stock') {
      const d = action.data;
      const today = defaultToday;
      const shipmentSeq = (shipments || []).length + 1;
      const shipmentId = `SHP-${today.slice(2).replace(/-/g, '')}-${String(shipmentSeq).padStart(3, '0')}`;

      const newShipment = {
        id: shipmentId,
        status: 'pending',
        supplier: d.supplier || '',
        invoiceNumber: d.invoiceNumber || '',
        invoiceDate: d.invoiceDate || today,
        expectedDate: d.expectedDate || null,
        createdAt: now.toISOString(),
        createdBy: user.id,
        receivedAt: null,
        receivedBy: null,
        taskId: null,
        notes: '',
        items: (d.items || []).map((item, idx) => ({
          id: `item-${idx + 1}`,
          name: item.name,
          expectedQty: item.qty || 1,
          actualQty: null,
          unitPrice: item.unitPrice || 0,
          location: null,
          productId: item.productId || null,
          status: 'pending',
          notes: '',
        })),
        discrepancies: [],
      };

      setShipments(prev => [newShipment, ...(prev || [])]);

      // Auto-create receiving task for staff
      const staffUsers = (users || []).filter(u => u.role === 'staff' || u.role === 'store_manager');
      const targetStaff = staffUsers[0];
      const taskId = Date.now();
      const taskDate = d.expectedDate || today;
      const taskText = `📦 ${t('shipment.pending')}: ${d.supplier || '?'} - ${d.items?.length || 0}${t('shipment.itemCount', { count: '' }).replace('{count}', '')}`;

      const newTask = {
        id: taskId,
        text: taskText,
        done: false,
        date: taskDate,
        createdAt: now.toISOString(),
        from: user.id,
        fromName: user.name,
        target: targetStaff?.id || 'U002',
        targetName: targetStaff?.name || '',
        shipmentId: shipmentId,
        type: 'receiving',
      };

      setTasks(prev => [newTask, ...(prev || [])]);

      // Update shipment with task reference
      setShipments(prev => (prev || []).map(s => s.id === shipmentId ? { ...s, taskId } : s));

      const totalCost = (d.items || []).reduce((s, i) => s + (i.unitPrice || 0) * (i.qty || 1), 0);
      let resultMsg = `✅ ${t('ai.preStockCreated', { id: shipmentId, count: d.items?.length || 0, supplier: d.supplier || '?' })}`;
      resultMsg += `\n💰 ${t('shipment.totalCost')}: ¥${totalCost.toLocaleString()}`;
      resultMsg += `\n📋 ${t('ai.preStockTaskCreated')}`;
      return resultMsg;
    }

    return `⚠️ ${t('ai.unknownAction')}`;
  };

  const handleConfirm = () => {
    if (!pendingAction) return;
    const result = executeAction(pendingAction);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: result,
      displayText: result,
    }]);
    setPendingAction(null);
  };

  const handleCancel = () => {
    setPendingAction(null);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `❌ ${t('ai.actionCancelled')}`,
      displayText: `❌ ${t('ai.actionCancelled')}`,
    }]);
  };

  // Collapsed card (only in non-modal mode)
  if (!expanded && !isModal) {
    return (
      <div className="bg-gradient-to-br from-indigo-500/10 to-purple-600/10 border border-indigo-500/20 rounded-2xl overflow-hidden">
        <button
          onClick={() => setExpanded(true)}
          className="w-full px-4 py-4 flex items-center gap-3 active:bg-indigo-500/10 transition"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Icon name="Sparkles" size={20} className="text-white" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-t-primary font-medium text-sm">{t('ai.title')}</div>
            <div className="text-t-tertiary text-xs">{t('ai.welcomeHint')}</div>
          </div>
          <Icon name="ChevronDown" size={18} className="text-t-secondary" />
        </button>
        {/* Quick action chips */}
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto">
          {[
            { label: t('ai.quickOrder'), icon: 'ShoppingBag' },
            { label: t('ai.quickStock'), icon: 'Package', isIntake: true },
            { label: t('ai.quickPreStock'), icon: 'Truck' },
            { label: t('ai.quickSearch'), icon: 'Search' },
          ].map(q => (
            <button
              key={q.label}
              onClick={() => {
                if (q.isIntake && onOpenIntake) {
                  onOpenIntake();
                } else {
                  setExpanded(true); setInput(q.label);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-t-card/80 border border-t-border rounded-full text-t-secondary text-xs whitespace-nowrap hover:border-indigo-500/40 transition active:scale-95"
            >
              <Icon name={q.icon} size={12} />
              {q.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Expanded chat card
  const wrapperClass = isModal
    ? 'flex flex-col h-full bg-t-bg'
    : 'bg-t-card border border-indigo-500/20 rounded-2xl overflow-hidden';

  return (
    <div className={wrapperClass}>
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-t-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Icon name="Sparkles" size={16} className="text-white" />
          </div>
          <div className="text-t-primary font-medium text-sm">{t('ai.title')}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {messages.length > 0 && (
            <button
              onClick={() => { setMessages([]); setPendingAction(null); }}
              className="w-7 h-7 rounded-lg bg-t-surface flex items-center justify-center text-t-muted hover:text-t-primary transition"
            >
              <Icon name="Trash2" size={14} />
            </button>
          )}
          <button
            onClick={() => isModal ? onClose?.() : setExpanded(false)}
            className="w-7 h-7 rounded-lg bg-t-surface flex items-center justify-center text-t-muted hover:text-t-primary transition"
          >
            <Icon name={isModal ? 'X' : 'ChevronUp'} size={16} />
          </button>
        </div>
      </div>

      {/* Chat area - scrollable with max height */}
      <div className={`overflow-y-auto p-3 space-y-2.5 ${isModal ? 'flex-1' : ''}`} style={isModal ? {} : { maxHeight: '360px' }}>
        {messages.length === 0 && (
          <div className="text-center py-6">
            <div className="text-t-muted text-xs mb-3">{t('ai.welcome')}</div>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {[
                { label: t('ai.quickOrder'), isIntake: false },
                { label: t('ai.quickStock'), isIntake: true },
                { label: t('ai.quickSearch'), isIntake: false },
              ].map(q => (
                <button
                  key={q.label}
                  onClick={() => {
                    if (q.isIntake && onOpenIntake) {
                      onOpenIntake();
                      onClose?.();
                    } else {
                      setInput(q.label);
                    }
                  }}
                  className="px-2.5 py-1.5 bg-t-surface border border-t-border rounded-lg text-t-tertiary text-xs hover:border-indigo-500/40 hover:text-t-primary transition"
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.filter(m => m.displayText).map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${
              msg.role === 'user'
                ? 'bg-indigo-500 text-white'
                : 'bg-t-surface border border-t-border text-t-primary'
            }`}>
              {msg.hasImage && <div className="text-xs opacity-70 mb-1">📷 {msg.imageCount > 1 ? t('ai.imagesAttached', { count: msg.imageCount }) : t('ai.imageAttached')}</div>}
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.displayText}</div>
              {msg.action && (
                <div className="mt-1.5 pt-1.5 border-t border-white/10">
                  <div className="text-xs opacity-70">
                    {msg.action.type === 'create_order' && `📋 ${t('ai.orderSummary', { count: msg.action.data.items?.length || 0 })}`}
                    {msg.action.type === 'stock_in' && `📥 ${t('ai.stockInSummary', { count: msg.action.data.items?.length || 0 })}`}
                    {msg.action.type === 'stock_adjust' && `🔄 ${t('ai.adjustSummary', { count: msg.action.data.items?.length || 0 })}`}
                    {msg.action.type === 'pre_stock' && `📦 ${t('ai.preStockSummary', { count: msg.action.data.items?.length || 0 })}`}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-t-surface border border-t-border rounded-2xl px-3 py-2">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Pending action confirmation */}
        {pendingAction && (
          <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-3">
            <div className="text-indigo-300 text-xs font-medium mb-2">{t('ai.confirmAction')}</div>

            {pendingAction.type === 'create_order' && pendingAction.data.items && (
              <div className="space-y-1 mb-2">
                {pendingAction.data.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-t-primary">{item.name} ×{item.qty}</span>
                    <span className="text-t-accent-text">¥{(item.price * item.qty).toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-indigo-500/30 pt-1 flex justify-between text-xs font-bold">
                  <span className="text-t-primary">{t('ai.total')}</span>
                  <span className="text-t-accent-text">¥{pendingAction.data.items.reduce((s, i) => s + i.price * i.qty, 0).toLocaleString()}</span>
                </div>
                {pendingAction.data.customer && (
                  <div className="text-xs text-t-tertiary">{t('ai.customer')}: {pendingAction.data.customer}</div>
                )}
              </div>
            )}

            {pendingAction.type === 'stock_in' && pendingAction.data.items && (
              <div className="space-y-1 mb-2">
                {pendingAction.data.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-t-primary">{item.name} ×{item.qty}</span>
                    {item.cost > 0 && <span className="text-t-tertiary">@¥{item.cost.toLocaleString()}</span>}
                  </div>
                ))}
                {pendingAction.data.source && (
                  <div className="text-xs text-t-tertiary">{t('ai.source')}: {pendingAction.data.source}</div>
                )}
              </div>
            )}

            {pendingAction.type === 'stock_adjust' && pendingAction.data.items && (
              <div className="space-y-1 mb-2">
                {pendingAction.data.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-t-primary">{item.name}</span>
                    <span className="text-amber-400">→ {item.newStock}{t('ai.bottles')}</span>
                  </div>
                ))}
              </div>
            )}

            {pendingAction.type === 'pre_stock' && pendingAction.data.items && (
              <div className="space-y-1 mb-2">
                <div className="text-xs text-t-tertiary mb-1">
                  🚚 {pendingAction.data.supplier || '?'} {pendingAction.data.expectedDate ? `→ ${pendingAction.data.expectedDate}` : ''}
                </div>
                {pendingAction.data.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-t-primary">{item.name} ×{item.qty}</span>
                    <span className="text-t-tertiary">@¥{(item.unitPrice || 0).toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-indigo-500/30 pt-1 flex justify-between text-xs font-bold">
                  <span className="text-t-primary">{t('shipment.totalCost')}</span>
                  <span className="text-t-accent-text">¥{pendingAction.data.items.reduce((s, i) => s + (i.unitPrice || 0) * (i.qty || 1), 0).toLocaleString()}</span>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={handleCancel} className="flex-1 py-1.5 rounded-lg bg-t-surface text-t-tertiary text-xs font-medium">
                {t('ai.cancel')}
              </button>
              <button onClick={handleConfirm} className="flex-1 py-1.5 rounded-lg bg-indigo-500 text-white text-xs font-bold">
                {t('ai.confirm')}
              </button>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-t-border p-2.5 flex-shrink-0">
        <div className="flex gap-1.5 items-end">
          {/* Image upload button (camera/gallery/file) */}
          <button
            onClick={() => galleryRef.current?.click()}
            className="w-8 h-8 rounded-lg bg-t-surface flex items-center justify-center text-t-tertiary hover:text-t-primary transition flex-shrink-0"
            title={t('ai.selectImage')}
          >
            <Icon name="ImagePlus" size={16} />
          </button>
          <input type="file" ref={galleryRef} accept="image/*" multiple onChange={handleImageUpload} className="hidden" />

          <div className="flex-1 bg-t-surface border border-t-border rounded-lg flex items-end">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t('ai.inputPlaceholder')}
              rows={1}
              className="flex-1 bg-transparent px-3 py-2 text-t-primary text-sm placeholder-t-muted focus:outline-none resize-none max-h-20"
              style={{ minHeight: '36px' }}
            />
          </div>

          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || loading}
            className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center text-white disabled:opacity-50 flex-shrink-0 transition hover:bg-indigo-400"
          >
            <Icon name="Send" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
